import React, { type PropsWithChildren, useEffect, useMemo, useState } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
import UFOSegment from '@atlaskit/react-ufo/segment';
import { type SideBySideExperiment } from '@atlassian/search-experiment';

import { PrimaryProductKeys } from '../../common/constants/products';
import { type onInputDropdownResultSelect, type onInputSubmit } from '../../common/constants/types';
import { type AppContextProviderProps } from '../../common/ui/app-context';
import { WidthObserverProvider } from '../../common/ui/width-observer';
import { OAuthSource } from '../../common/utils/events/oauth-events';
import { onTrackAvailableProducts } from '../../common/utils/events/track-available-products';
import { OAuthContainer } from '../../common/utils/oauth-container';
import { useAnalytics } from '../../controllers/analytics';
import { ErrorBoundary } from '../../controllers/error-boundary';
import { useOAuthHandlers } from '../../controllers/oauth';
import { useBootstrap, useIsHello } from '../../controllers/store/bootstrap';
import { type BootstrapContainerProps } from '../../controllers/store/bootstrap/bootstrap-store-provider';

import { BulkConnectModal } from './bulk-connect-modal';
import Filters from './filters';
import Input from './input';
import { Layout, LayoutSideBySide } from './layout';
import { LoadUFOExperienceSuccess } from './load-success';
import { ResultsContainer } from './results';
import AutoCorrection from './results/auto-correction';
import AssetsAppMessageBanner from './results/results/assets-app-message-banner';
import NewProductMessageBanner from './results/results/new-product-message-banner';
import RightPanel from './right-panel';
import ShellWithErrorBoundary, { Shell } from './shell';
import SmartAnswers from './smart-answers';

// Props of the SearchPage component are passed inconsistently through 'shell' or the root level.
// Until we consolidate that we need to expose different props in different ways (also to avoid breaking changes).
// The two types below will be merged into one once we execute the props redesign from:
// https://hello.atlassian.net/wiki/spaces/~sogrady/pages/4408832752/RFC+Search+page+props+redesign
type AppContextProviderPropsExposedThroughShell = Pick<
	AppContextProviderProps,
	'isNav4Enabled' | 'isAdminHubAIEnabled' | 'isRovoLLMEnabled'
>;

// Expose only the props that are needed at the root level.
type AppContextProviderPropsExposedThroughRootLevel = Pick<
	AppContextProviderProps,
	'mapUserIdToProfileUrl' | 'generateSearchUrl' | 'onNavigate' | 'onSearchResultsShown'
>;

export type SearchPageProps = {
	shell: Omit<BootstrapContainerProps, 'features' | 'products'> &
		AppContextProviderPropsExposedThroughShell;
	callbacks?: BootstrapContainerProps['callbacks'];
} & AppContextProviderPropsExposedThroughRootLevel;

/**
 * Deprecated: non-relay search-page
 * This is the main component which is exposed from the search-page
 * package.
 * The motivation is for consumers do a one-component bootstrap.
 */
export const SearchPage = ({
	shell,
	callbacks,
	mapUserIdToProfileUrl,
	generateSearchUrl,
	onNavigate,
	onSearchResultsShown,
}: PropsWithChildren<SearchPageProps>): React.JSX.Element => {
	const [{ thirdPartyConfigs, products: availableProducts }] = useBootstrap();
	const { searchSessionId } = shell;
	const { fireAnalyticsEvent } = useAnalytics();
	const [isHello] = useIsHello();
	const [hasRecordedAvailableProducts, setHasRecordedAvailableProducts] = useState(false);

	// Log a health metric for accessible products
	useEffect(() => {
		if (fg('rovo-search-accessible-products-healthmetric')) {
			// When null or empty bootstrap isn't ready. Even failed bootstrap will have primaryProduct in the set
			if (availableProducts && availableProducts.length > 0 && !hasRecordedAvailableProducts) {
				fireAnalyticsEvent(
					onTrackAvailableProducts({
						availableProducts,
						searchSessionId,
					}),
				);
				setHasRecordedAvailableProducts(true);
			}
		}
	}, [availableProducts, hasRecordedAvailableProducts, fireAnalyticsEvent, searchSessionId]);

	return (
		<UFOSegment name="search-page">
			<ShellWithErrorBoundary
				{...shell}
				callbacks={callbacks}
				mapUserIdToProfileUrl={mapUserIdToProfileUrl}
				generateSearchUrl={generateSearchUrl}
				onNavigate={onNavigate}
				onSearchResultsShown={onSearchResultsShown}
			>
				<WidthObserverProvider>
					<Layout
						components={{
							input: <Input />,
							filters: <Filters />,
							results: <ResultsContainer />,
							smartAnswers: <SmartAnswers />,
							oauthContainer: (
								<ErrorBoundary section="SearchPageOAuthContainer">
									<OAuthContainer
										thirdPartyConfigs={thirdPartyConfigs}
										availableProducts={availableProducts}
										isHello={isHello}
										useOAuthHandlers={useOAuthHandlers}
										fireAnalyticsEvent={fireAnalyticsEvent}
									/>
								</ErrorBoundary>
							),
							rightPanel: <RightPanel />,
							autoCorrection: <AutoCorrection />,
							messageBanner: (
								<>
									{!fg('assets_rovo_search_all') &&
										shell.primaryProduct === PrimaryProductKeys.Assets && (
											<AssetsAppMessageBanner />
										)}
									<NewProductMessageBanner />
								</>
							),
							bulkConnectModal: (
								<ErrorBoundary section="SearchPageBulkConnectModal">
									<BulkConnectModal
										isOnSearchDialog={false}
										oAuthSource={OAuthSource.searchPageBulkConnect}
									/>
								</ErrorBoundary>
							),
						}}
					/>
				</WidthObserverProvider>
				<LoadUFOExperienceSuccess />
			</ShellWithErrorBoundary>
		</UFOSegment>
	);
};

export type SearchPageSideBySideProps = SearchPageProps & {
	experiments?: SideBySideExperiment[];
};

export const SearchPageSideBySide = ({
	shell,
	callbacks,
	mapUserIdToProfileUrl,
	experiments,
}: PropsWithChildren<SearchPageSideBySideProps>): React.JSX.Element => {
	const [{ thirdPartyConfigs, products: availableProducts }] = useBootstrap();
	const { fireAnalyticsEvent } = useAnalytics();
	const [isHello] = useIsHello();

	const shellWithSideBySide = useMemo(
		() => ({
			...shell,
			features: {
				sideBySide: {
					enabled: true,
					experiments,
				},
			},
		}),
		[shell, experiments],
	);

	return (
		<UFOSegment name="search-page-sbs">
			<Shell
				{...shellWithSideBySide}
				callbacks={callbacks}
				mapUserIdToProfileUrl={mapUserIdToProfileUrl}
			>
				<WidthObserverProvider>
					<LayoutSideBySide
						components={{
							filters: <Filters />,
							results: <ResultsContainer />,
							smartAnswers: <SmartAnswers />,
							oauthContainer: (
								<ErrorBoundary section="SearchPageSideBySideOAuthContainer">
									<OAuthContainer
										isOnSearchDialog
										thirdPartyConfigs={thirdPartyConfigs}
										availableProducts={availableProducts}
										isHello={isHello}
										useOAuthHandlers={useOAuthHandlers}
										fireAnalyticsEvent={fireAnalyticsEvent}
									/>
								</ErrorBoundary>
							),
							autoCorrection: <AutoCorrection />,
						}}
					/>
				</WidthObserverProvider>
				<LoadUFOExperienceSuccess />
			</Shell>
		</UFOSegment>
	);
};

export type SearchPageBrowserExtensionProps = SearchPageProps & {
	onInputDropdownResultSelect: onInputDropdownResultSelect;
	onInputSubmit: onInputSubmit;
};

export const SearchPageBrowserExtension = ({
	shell,
	callbacks,
	mapUserIdToProfileUrl,
	onInputDropdownResultSelect = () => {},
	onInputSubmit = () => {},
}: PropsWithChildren<SearchPageBrowserExtensionProps>): React.JSX.Element => {
	const shellWithChromeExtension = useMemo(
		() => ({
			...shell,
			thirdPartyDisabled: true,
			features: {
				chromeExtension: {
					enabled: true,
					onInputDropdownResultSelect,
					onInputSubmit,
				},
			},
		}),
		[onInputDropdownResultSelect, onInputSubmit, shell],
	);

	return (
		<UFOSegment name="search-page-browser-extension">
			<Shell
				{...shellWithChromeExtension}
				callbacks={callbacks}
				mapUserIdToProfileUrl={mapUserIdToProfileUrl}
			>
				<WidthObserverProvider>
					<Input />
				</WidthObserverProvider>
			</Shell>
		</UFOSegment>
	);
};
